import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, Edit2, CheckCircle, XCircle } from "lucide-react";
import {
  uploadArquivo,
  deleteArquivo,
  updateArquivoMetadata,
  addArquivoToRecord,
  ArquivoData,
} from "@/integrations/supabase/arquivos";
import { toast } from "sonner";

interface ArquivosSectionProps {
  tableType: "quotes" | "orders";
  recordId: string;
  arquivos: ArquivoData[];
  onArquivosUpdated: (arquivos: ArquivoData[]) => void;
  isExpanded?: boolean;
}

export default function ArquivosSection({
  tableType,
  recordId,
  arquivos,
  onArquivosUpdated,
  isExpanded = false,
}: ArquivosSectionProps) {
  const [isExpanding, setIsExpanding] = useState(isExpanded);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [posicao, setPosicao] = useState("");
  const [descricao, setDescricao] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPosicao, setEditPosicao] = useState("");
  const [editDescricao, setEditDescricao] = useState("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !posicao.trim()) {
      toast.error("Selecione um arquivo e informe a posição");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadResult = await uploadArquivo(selectedFile, posicao, descricao);

      if (!uploadResult.ok) {
        toast.error(uploadResult.error || "Erro no upload");
        setIsUploading(false);
        return;
      }

      const novoArquivo = uploadResult.data!;

      // Simular progress para UI feedback
      for (let i = 0; i < 100; i++) {
        await new Promise((resolve) => setTimeout(resolve, 10));
        setUploadProgress(i + 1);
      }

      // ✅ Salvar arquivo no banco de dados
      const saveResult = await addArquivoToRecord(tableType, recordId, novoArquivo);
      if (!saveResult.ok) {
        toast.error(saveResult.error || "Erro ao salvar arquivo no banco de dados");
        setIsUploading(false);
        return;
      }

      // Atualizar lista local
      const arquivosAtualizados = [...arquivos, novoArquivo];
      onArquivosUpdated(arquivosAtualizados);

      toast.success("Arquivo enviado com sucesso!");
      setSelectedFile(null);
      setPosicao("");
      setDescricao("");
      setUploadProgress(0);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao fazer upload");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (arquivoId: string) => {
    if (!confirm("Tem certeza que deseja remover este arquivo?")) return;

    try {
      const result = await deleteArquivo(tableType, recordId, arquivoId);

      if (!result.ok) {
        toast.error(result.error || "Erro ao remover arquivo");
        return;
      }

      const arquivosAtualizados = arquivos.filter((a) => a.id !== arquivoId);
      onArquivosUpdated(arquivosAtualizados);

      toast.success("Arquivo removido com sucesso!");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao remover arquivo");
    }
  };

  const handleSaveEdit = async (arquivoId: string) => {
    try {
      const result = await updateArquivoMetadata(tableType, recordId, arquivoId, {
        posicao: editPosicao,
        descricao: editDescricao,
      });

      if (!result.ok) {
        toast.error(result.error || "Erro ao atualizar arquivo");
        return;
      }

      const arquivosAtualizados = arquivos.map((a) =>
        a.id === arquivoId
          ? { ...a, posicao: editPosicao, descricao: editDescricao }
          : a
      );
      onArquivosUpdated(arquivosAtualizados);

      toast.success("Arquivo atualizado com sucesso!");
      setEditingId(null);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao atualizar arquivo");
    }
  };

  const startEdit = (arquivo: ArquivoData) => {
    setEditingId(arquivo.id);
    setEditPosicao(arquivo.posicao);
    setEditDescricao(arquivo.descricao || "");
  };

  return (
    <Card className="border border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Arquivo / Arte</CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanding(!isExpanding)}
          >
            {isExpanding ? "▼" : "▶"}
          </Button>
        </div>
      </CardHeader>

      {isExpanding && (
        <CardContent className="space-y-4">
          {/* Upload Form */}
          <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
            <div>
              <Label htmlFor="arquivo">Selecionar arquivo</Label>
              <input
                id="arquivo"
                type="file"
                onChange={handleFileChange}
                disabled={isUploading}
                accept="image/*,.pdf"
                className="hidden"
              />
              <label
                htmlFor="arquivo"
                className={`block border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                  isUploading
                    ? "border-blue-400 bg-blue-50"
                    : selectedFile
                    ? "border-green-400 bg-green-50"
                    : "border-input hover:border-secondary"
                }`}
              >
                {isUploading ? (
                  <div className="space-y-2">
                    <div className="w-6 h-6 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-blue-600 font-medium">
                      Enviando {Math.round(uploadProgress)}%
                    </p>
                  </div>
                ) : selectedFile ? (
                  <div className="space-y-1">
                    <CheckCircle className="w-6 h-6 mx-auto text-green-600" />
                    <p className="text-sm text-green-600 font-medium">
                      {selectedFile.name}
                    </p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Clique ou arraste o arquivo
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PNG, JPG, PDF até 10MB
                    </p>
                  </div>
                )}
              </label>
            </div>

            <div>
              <Label htmlFor="posicao">Posição / Localização *</Label>
              <Input
                id="posicao"
                placeholder="ex: Frente, Verso, Logo, Etiqueta..."
                value={posicao}
                onChange={(e) => setPosicao(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div>
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Input
                id="descricao"
                placeholder="ex: Logo do cliente, arte da tampa..."
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !posicao.trim() || isUploading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              Enviar arquivo
            </Button>
          </div>

          {/* Lista de Arquivos */}
          {arquivos.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                Arquivos ({arquivos.length})
              </p>
              <div className="space-y-2">
                {arquivos.map((arquivo) => (
                  <div
                    key={arquivo.id}
                    className="border rounded-lg p-3 space-y-2 bg-background"
                  >
                    {editingId === arquivo.id ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="Posição"
                          value={editPosicao}
                          onChange={(e) => setEditPosicao(e.target.value)}
                        />
                        <Input
                          placeholder="Descrição"
                          value={editDescricao}
                          onChange={(e) => setEditDescricao(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(arquivo.id)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Salvar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingId(null)}
                            className="flex-1"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex gap-3">
                          {/* Miniatura da imagem */}
                          {arquivo.mimetype.startsWith("image/") && (
                            <div className="flex-shrink-0">
                              <img
                                src={arquivo.url}
                                alt={arquivo.nome}
                                className="w-20 h-20 object-cover rounded border border-border"
                              />
                            </div>
                          )}
                          {arquivo.mimetype === "application/pdf" && (
                            <div className="flex-shrink-0 w-20 h-20 rounded border border-border bg-red-50 flex items-center justify-center">
                              <div className="text-center">
                                <p className="text-xs font-bold text-red-600">PDF</p>
                              </div>
                            </div>
                          )}

                          {/* Detalhes */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {arquivo.nome}
                            </p>
                            <p className="text-xs text-blue-600 font-semibold mt-1">
                              📍 {arquivo.posicao}
                            </p>
                            {arquivo.descricao && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {arquivo.descricao}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {arquivo.tamanho_kb}KB
                            </p>
                          </div>

                          {/* Botões de ação */}
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEdit(arquivo)}
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(arquivo.id)}
                              title="Deletar"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>

                        {/* Link para visualizar em tela cheia */}
                        <a
                          href={arquivo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline block mt-2"
                        >
                          🔍 Ver em tela cheia
                        </a>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
